import { useSelector } from 'react-redux'
import { getItemsForCurrentMenu } from '../../selectors/items'
import { getCurrentMenuCategories } from '../../selectors/categories'
import {
  ALL_ID,
  FAVORITES_ID,
  getCategorySearch,
  getItemFavorites,
  getLastUpdatedFavorites,
  getSelectedCategory,
} from '../../VNCategories/Selectors'
import { useCallback } from 'react'
import { sortItemCategory } from '../../selectors/itemCategory'
import { getBarcodeScanningSettings } from '../../selectors/config'
import { hasRequiredModifiers } from '../../utils/orderUtils'

const useMenuItems = () => {
  const menuItems = useSelector(getItemsForCurrentMenu)
  const categories = useSelector(getCurrentMenuCategories)
  const selectedCategory = useSelector(getSelectedCategory)
  const itemFavorites = useSelector(getItemFavorites)
  const lastUpdatedFavorites = useSelector(getLastUpdatedFavorites)
  const searchTerm = useSelector(getCategorySearch)
  const barcodeScanningSettings = useSelector(getBarcodeScanningSettings)

  /**
   * Get menu items with category filtering and item searching applied
   * This was originally in a selector, but this type of mapping is not conducive to memoization
   * due to the number of factors that can change (categories, favorites, search term, etc)
   *
   * @returns {Array} - Array of menu items
   */
  const getItems = useCallback(() => {
    const useDefaultCategory = selectedCategory === ALL_ID || !selectedCategory
    const isFavoritesCategory = selectedCategory === FAVORITES_ID
    const items = Object.keys(menuItems?.byId || {}).reduce((acc, itemId) => {
      const item = menuItems.byId[itemId] || {}
      const itemCategoryUuids = item.itemCategoryUuids || []
      const status = item.variants?.[0]?.inventoryState || 'available'
      const orderMaxAmount = parseInt(item.orderMaxAmount ?? 999)
      const orderMinCount = parseInt(item.orderMinCount ?? 1)
      const inventory = item.inventory || 100
      const hasReqMods = hasRequiredModifiers(item)

      // Skip modifiers
      if (item.modifier) return acc

      // Category filtering
      if (!isFavoritesCategory && !useDefaultCategory && !itemCategoryUuids.includes(selectedCategory)) {
        return acc
      }

      if (!barcodeScanningSettings.displayScannableItems && barcodeScanningSettings.barcodeScanningEnabled && item?.barcode) {
        return acc
      }

      // Search by item name
      const itemName = item.name?.toLowerCase?.() || ''
      const searchQuery = searchTerm?.toLowerCase() || ''
      if (searchTerm?.length && !itemName.includes(searchQuery)) {
        return acc
      }
      const price = item?.defaultPriceInCents !== null ? `$${(item?.defaultPriceInCents / 100)?.toFixed(2)}` : '--'

      const baseItem = {
        ...item,
        price,
        categoryId: item.itemCategoryUuid || null,
        color: categories?.byId?.[item.itemCategoryUuid]?.color ?? null,
        hasRequiredModifiers: hasReqMods,
        status,
        orderMaxAmount,
        orderMinCount,
        inventory,
        itemCategoryUuid: useDefaultCategory ? item.itemCategoryUuid : selectedCategory,
        isFavorite: itemFavorites[itemId]?.includes(item.itemCategoryUuid) || false,
        favoriteUpdatedAt: lastUpdatedFavorites?.[itemId]?.[item.itemCategoryUuid] || null,
      }

      // Multi-category handling
      if (itemCategoryUuids.length > 1 && useDefaultCategory) {
        itemCategoryUuids.forEach((categoryId) => {
          acc.push({
            ...baseItem,
            itemCategoryUuid: categoryId || null,
            color: categories?.byId?.[categoryId]?.color ?? null,
            isFavorite: itemFavorites[itemId]?.includes(categoryId) || false,
            favoriteUpdatedAt: lastUpdatedFavorites?.[itemId]?.[categoryId] || null,
          })
        })
        return acc
      }

      // Favorites category handling
      if (isFavoritesCategory) {
        const itemFavoriteCats = itemFavorites[itemId] || []

        // Skip items not in favorites
        if (itemFavoriteCats.length === 0) {
          return acc
        }

        // item has multiple categories or is uncategorized
        const catIntersection = (item.itemCategoryUuids || []).filter?.((cat) => itemFavoriteCats.includes(cat))
        if (catIntersection.length === 0 && item.itemCategoryUuid !== null) {
          return acc
        }
        acc.push({
          ...baseItem,
          color: categories?.byId?.[catIntersection[0]]?.color ?? null,
          isFavorite: true,
          favoriteUpdatedAt: lastUpdatedFavorites?.[itemId]?.[catIntersection[0]] || null,
        })
        return acc
      }

      const curCategoryId = useDefaultCategory ? item.itemCategoryUuid : selectedCategory

      // Single category or uncategorized item
      acc.push({
        ...baseItem,
        itemCategoryUuid: curCategoryId || null,
        color: categories?.byId?.[curCategoryId]?.color ?? null,
        isFavorite: itemFavorites[itemId]?.includes(curCategoryId) || false,
        favoriteUpdatedAt: lastUpdatedFavorites?.[itemId]?.[curCategoryId] || null,
      })

      return acc
    }, [])

    // Group items by their category UUID using native reduce
    const groups = items.reduce((acc, item) => {
      const categoryId = item.itemCategoryUuid || null
      if (!acc[categoryId]) {
        acc[categoryId] = []
      }
      acc[categoryId].push(item)
      return acc
    }, {})

    // return favorites category items if selected
    if (isFavoritesCategory) {
      // Return a stringified version to avoid re-renders
      return groups[FAVORITES_ID] || []
    }

    // Create a sorted list of category IDs (including null for uncategorized)
    const sortedCategoryIds = sortItemCategory(categories?.byId).map((category) => category.itemCategoryUuid || null)

    // Flatten grouped items in the order of sortedCategoryIds - "null" for uncategorized items
    return [...sortedCategoryIds, null].flatMap((categoryId) => groups[categoryId] || [])
  }, [categories, itemFavorites, lastUpdatedFavorites, menuItems, searchTerm, selectedCategory, barcodeScanningSettings])

  return { getItems }
}

export default useMenuItems
